/**
 * Form submit guard functionality
 * Prevents double form submissions by disabling submit buttons on form submit
 * and showing a loading spinner for visual feedback
 */
export class FormSubmitGuard {
    /**
     * Initialize form submit guard functionality across the document
     */
    public static initialize(): void {
        document.addEventListener('DOMContentLoaded', () => {
            FormSubmitGuard.initializeForms();
            FormSubmitGuard.initializeButtonLinks();
        });
        window.addEventListener('pageshow', FormSubmitGuard.handlePageRestore.bind(FormSubmitGuard));
    }

    /**
     * Initialize form submission guards
     */
    private static initializeForms(): void {
        const forms = document.querySelectorAll('form');

        forms.forEach(form => {
            const formElement = form as HTMLFormElement;
            
            // Find submit buttons within the form
            const internalButtons = formElement.querySelectorAll(
                'input[type="submit"], button[type="submit"]'
            );

            // Find external buttons that reference this form via form attribute
            let externalButtons: NodeListOf<Element> = document.querySelectorAll('__none__');
            if (formElement.id) {
                externalButtons = document.querySelectorAll(
                    `button[type="submit"][form="${formElement.id}"], input[type="submit"][form="${formElement.id}"]`
                );
            }

            // Only create guard if form has submit buttons
            if (internalButtons.length > 0 || externalButtons.length > 0) {
                new FormSubmitGuard(formElement, internalButtons, externalButtons);
            }
        });
    }

    /**
     * Initialize click guards for anchor tags styled as buttons.
     * When any button link is clicked, show spinner on that button
     * and disable all other button links on the page.
     */
    private static initializeButtonLinks(): void {
        // Find anchor tags styled as action buttons
        const buttonLinks = Array.from(
            document.querySelectorAll('a.btn-primary, a.btn-light, a.btn-secondary')
        ) as HTMLAnchorElement[];

        // Skip if no button links found
        if (buttonLinks.length === 0) {
            return;
        }

        const spinnerHtml = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        buttonLinks.forEach(anchor => {
            anchor.addEventListener('click', (event) => {
                // Prevent any clicks if already navigating
                if (FormSubmitGuard.isNavigating) {
                    event.preventDefault();
                    return;
                }

                FormSubmitGuard.isNavigating = true;

                // Show spinner on the clicked button. Pin the rendered width
                // before swapping content so the button doesn't shrink to
                // spinner width and cause layout jitter.
                const originalText = anchor.textContent?.trim() || 'Continue';
                anchor.setAttribute('aria-label', `${originalText}, loading`);
                anchor.dataset.originalContent = anchor.innerHTML;
                anchor.style.minWidth = `${anchor.offsetWidth}px`;
                anchor.innerHTML = spinnerHtml;

                // Disable ALL button links on the page
                buttonLinks.forEach(link => {
                    link.classList.add('disabled');
                    link.setAttribute('aria-disabled', 'true');
                });
            });
        });
    }

    private static isNavigating = false;
    private static instances: FormSubmitGuard[] = [];

    private form: HTMLFormElement;
    private submitButtons: (HTMLInputElement | HTMLButtonElement)[];
    private isSubmitting: boolean = false;

    /**
     * Create a new form submit guard for a specific form
     */
    constructor(
        form: HTMLFormElement,
        internalButtons: NodeListOf<Element>,
        externalButtons: NodeListOf<Element>
    ) {
        this.form = form;
        
        // Combine internal and external buttons into a single array
        this.submitButtons = [
            ...Array.from(internalButtons),
            ...Array.from(externalButtons)
        ] as (HTMLInputElement | HTMLButtonElement)[];

        // Add submit event listener to the form
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        FormSubmitGuard.instances.push(this);
    }

    /**
     * Handle form submit event
     */
    private handleSubmit(event: SubmitEvent): void {
        // Prevent double submission
        if (this.isSubmitting) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        // Check HTML5 validation first (runs synchronously)
        if (!this.form.checkValidity()) {
            return;
        }

        // Check for pre-existing jQuery validation errors
        if (this.hasValidationErrors()) {
            return;
        }

        // Preserve the submitter's name/value as a hidden input since
        // disabling the button will exclude it from the submitted form data
        const submitter = event.submitter as HTMLButtonElement | HTMLInputElement | null;
        if (submitter?.name) {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = submitter.name;
            hidden.value = submitter.value;
            this.form.appendChild(hidden);
        }

        // Mark as submitting and disable buttons
        this.isSubmitting = true;
        this.disableAllSubmitButtons();
        this.announceSubmitting();

        // jQuery unobtrusive validation runs after our handler,
        // so check again after a tick and reset if validation failed
        setTimeout(() => {
            if (this.hasValidationErrors()) {
                this.resetButtons();
            }
        }, 0);
    }

    /**
     * Check for jQuery unobtrusive validation error classes
     */
    private hasValidationErrors(): boolean {
        return !!this.form.querySelector(
            // ASP.NET Core adds input-validation-error to hidden inputs with ModelState errors, and jQuery unobtrusive validation ignores hidden inputs, leaving input-validation-error in place.
            '.input-validation-error:not([type="hidden"]), .field-validation-error:not(.field-validation-valid), .validation-summary-errors'
        );
    }

    /**
     * Disable all submit buttons and show loading spinner
     */
    private disableAllSubmitButtons(): void {
        this.form.setAttribute('aria-busy', 'true');

        const spinnerHtml = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        this.submitButtons.forEach(button => {
            if (button instanceof HTMLButtonElement) {
                // Store original content and text for reset
                button.dataset.originalContent = button.innerHTML;
                const originalText = button.textContent?.trim() || 'Submit';

                // Set aria-label to maintain accessible name when content is replaced
                button.setAttribute('aria-label', `${originalText}, submitting`);

                // Pin the rendered width before swapping content so the button
                // doesn't shrink to spinner width and cause layout jitter.
                button.style.minWidth = `${button.offsetWidth}px`;
                button.innerHTML = spinnerHtml;
            }
            button.disabled = true;
        });
    }

    /**
     * Reset buttons to original state (when validation fails after submit)
     */
    private resetButtons(): void {
        this.isSubmitting = false;
        this.form.removeAttribute('aria-busy');

        const announceEl = document.getElementById('form-submit-announce');
        if (announceEl) {
            announceEl.textContent = '';
        }

        this.submitButtons.forEach(button => {
            button.disabled = false;
            if (button instanceof HTMLButtonElement && button.dataset.originalContent) {
                button.innerHTML = button.dataset.originalContent;
                button.removeAttribute('aria-label');
                button.style.minWidth = '';
            }
        });
    }

    /**
     * Announce submitting state to screen readers
     */
    private announceSubmitting(): void {
        let announceEl = document.getElementById('form-submit-announce');
        if (!announceEl) {
            announceEl = document.createElement('div');
            announceEl.id = 'form-submit-announce';
            announceEl.className = 'visually-hidden';
            announceEl.setAttribute('aria-live', 'assertive');
            announceEl.setAttribute('aria-atomic', 'true');
            document.body.appendChild(announceEl);
        }
        announceEl.textContent = 'Submitting, please wait...';
    }

    /**
     * Handle page restore from bfcache (back/forward cache)
     * Resets all form submit buttons and anchor button links to their original state
     */
    private static handlePageRestore(event: PageTransitionEvent): void {
        if (!event.persisted) return;

        // Reset all form submit guards
        FormSubmitGuard.instances.forEach(instance => {
            instance.resetButtons();
        });

        // Reset anchor button links
        const anchorsToReset = document.querySelectorAll('a[data-original-content]');
        anchorsToReset.forEach(anchor => {
            const el = anchor as HTMLAnchorElement;
            el.innerHTML = el.dataset.originalContent || '';
            delete el.dataset.originalContent;
            el.classList.remove('disabled');
            el.removeAttribute('aria-disabled');
            el.removeAttribute('aria-label');
            el.style.minWidth = '';
        });

        // Reset navigation flag
        FormSubmitGuard.isNavigating = false;
    }
}

// Initialize the form submit guard functionality
FormSubmitGuard.initialize();
